import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Trash2, Edit2, X, ChevronDown, Loader2, UserPlus,
  UserMinus, Search, Home, Dumbbell, BookOpen, Users2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const GROUP_TYPES = ['House', 'Club', 'Sport', 'Committee', 'Other'];

const TYPE_ICONS = {
  House: Home,
  Club: BookOpen,
  Sport: Dumbbell,
  Committee: Users2,
  Other: Users,
};

const COLOR_DOTS = {
  red:    'bg-red-500',
  blue:   'bg-blue-500',
  green:  'bg-emerald-500',
  yellow: 'bg-yellow-400',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  pink:   'bg-pink-500',
  teal:   'bg-teal-500',
};

const TYPE_BADGE = {
  House:     'bg-red-100 text-red-700',
  Club:      'bg-blue-100 text-blue-700',
  Sport:     'bg-green-100 text-green-700',
  Committee: 'bg-purple-100 text-purple-700',
  Other:     'bg-slate-100 text-slate-600',
};

export default function StudentGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const [membersLoading, setMembersLoading] = useState(false);

  const [form, setForm] = useState({
    name: '', group_type: 'House', description: '', color: 'blue',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType ? `?group_type=${filterType}` : '';
      const res = await api.get(`/groups${params}`);
      setGroups(res.data || []);
    } catch { toast.error('Failed to load groups'); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/students?limit=100').then(r => setAllStudents(r.data || [])).catch(() => {});
  }, []);

  const loadMembers = async (group) => {
    setSelectedGroup(group);
    setMembersLoading(true);
    try {
      const res = await api.get(`/groups/${group.id}/members`);
      setMembers(res.data?.members || []);
    } catch { toast.error('Failed to load group members'); }
    finally { setMembersLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Group name is required');
    setSaving(true);
    try {
      if (editingGroup) {
        await api.put(`/groups/${editingGroup.id}`, form);
        toast.success('Group updated');
        // Refresh selected group if it's the one being edited
        if (selectedGroup?.id === editingGroup.id) {
          setSelectedGroup(prev => ({ ...prev, ...form }));
        }
      } else {
        await api.post('/groups', form);
        toast.success('Group created');
      }
      setShowForm(false);
      setEditingGroup(null);
      load();
    } catch (err) { toast.error(err?.detail || 'Failed to save group'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete "${group.name}"? All member assignments will be removed.`)) return;
    try {
      await api.delete(`/groups/${group.id}`);
      toast.success('Group deleted');
      if (selectedGroup?.id === group.id) setSelectedGroup(null);
      load();
    } catch (err) { toast.error(err?.detail || 'Cannot delete group'); }
  };

  const handleAddStudent = async (studentId) => {
    if (!selectedGroup) return;
    try {
      await api.post(`/groups/${selectedGroup.id}/members`, { student_id: studentId });
      toast.success('Student added to group');
      loadMembers(selectedGroup);
      load(); // refresh member count
    } catch (err) { toast.error(err?.detail || 'Failed to add student'); }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!selectedGroup) return;
    try {
      await api.delete(`/groups/${selectedGroup.id}/members/${studentId}`);
      toast.success('Student removed from group');
      loadMembers(selectedGroup);
      load();
    } catch (err) { toast.error(err?.detail || 'Failed to remove student'); }
  };

  const memberIds = new Set(members.map(m => m.id));
  const availableStudents = allStudents.filter(s =>
    !memberIds.has(s.id) &&
    (addSearch === '' ||
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(addSearch.toLowerCase()) ||
      (s.admission_number || '').toLowerCase().includes(addSearch.toLowerCase()))
  );

  const filteredMembers = members.filter(m =>
    memberSearch === '' ||
    `${m.first_name} ${m.last_name}`.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const groupsByType = GROUP_TYPES.reduce((acc, t) => {
    const items = groups.filter(g => g.group_type === t);
    if (items.length) acc[t] = items;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="student-groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Houses, Clubs & Sports
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage student groups — houses, clubs, sports teams, and committees
          </p>
        </div>
        <button
          data-testid="create-group-btn"
          onClick={() => { setForm({ name: '', group_type: 'House', description: '', color: 'blue' }); setEditingGroup(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" /> New Group
        </button>
      </div>

      {/* Type filter tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {['', ...GROUP_TYPES].map(t => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${
              filterType === t
                ? 'bg-[#2563eb] text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
            data-testid={`filter-${t || 'all'}`}
          >
            {t || 'All Groups'}
            {t && <span className="ml-1 text-xs opacity-70">({groups.filter(g => g.group_type === t).length})</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Groups list */}
        <div className="lg:col-span-1">
          {/* Create/Edit Form */}
          {showForm && (
            <div className="bg-white rounded-xl border border-blue-200 p-4 mb-4 shadow-sm" data-testid="group-form">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800 text-sm">{editingGroup ? 'Edit Group' : 'New Group'}</h3>
                <button onClick={() => setShowForm(false)}>
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              <div className="space-y-3">
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Group name (e.g. Red House, Drama Club)"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                  data-testid="group-name-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <select
                      value={form.group_type}
                      onChange={e => setForm(f => ({ ...f, group_type: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none appearance-none pr-7"
                      data-testid="group-type-select"
                    >
                      {GROUP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {Object.entries(COLOR_DOTS).slice(0, 6).map(([key, cls]) => (
                      <button
                        key={key}
                        onClick={() => setForm(f => ({ ...f, color: key }))}
                        className={`w-5 h-5 rounded-full ${cls} border-2 transition ${form.color === key ? 'border-slate-700 scale-110' : 'border-transparent'}`}
                      />
                    ))}
                  </div>
                </div>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description (optional)"
                  rows={2}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none resize-none"
                  data-testid="group-description-input"
                />
                <div className="flex gap-2">
                  <button onClick={() => setShowForm(false)} className="flex-1 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    data-testid="save-group-btn"
                  >
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {editingGroup ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Group cards */}
          {loading && !groups.length && (
            [...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse mb-3" />)
          )}

          {!loading && groups.length === 0 && !showForm && (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
              <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No groups yet</p>
              <button onClick={() => { setForm({ name: '', group_type: 'House', description: '', color: 'blue' }); setEditingGroup(null); setShowForm(true); }} className="text-xs text-blue-600 mt-2">Create first group →</button>
            </div>
          )}

          {Object.entries(groupsByType).map(([type, items]) => {
            const Icon = TYPE_ICONS[type] || Users;
            return (
              <div key={type} className="mb-4">
                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-2 flex items-center gap-1">
                  <Icon className="w-3 h-3" /> {type}s
                </p>
                <div className="space-y-2">
                  {items.map(g => (
                    <div
                      key={g.id}
                      data-testid={`group-card-${g.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`bg-white rounded-xl border p-3 cursor-pointer transition-all ${
                        selectedGroup?.id === g.id ? 'border-blue-400 ring-1 ring-blue-100 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => loadMembers(g)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${COLOR_DOTS[g.color] || 'bg-blue-500'}`} />
                          <span className="font-medium text-slate-800 text-sm">{g.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-400">{g.member_count}</span>
                          <button onClick={e => { e.stopPropagation(); setForm({ name: g.name, group_type: g.group_type, description: g.description, color: g.color }); setEditingGroup(g); setShowForm(true); }}
                            className="p-1 text-slate-400 hover:text-blue-600 rounded">
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button onClick={e => { e.stopPropagation(); handleDelete(g); }}
                            className="p-1 text-slate-400 hover:text-red-500 rounded">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {g.description && <p className="text-xs text-slate-400 mt-0.5 truncate">{g.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Members panel */}
        <div className="lg:col-span-2">
          {!selectedGroup ? (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-16 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="font-medium text-slate-500">Select a group to manage its members</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="group-members-panel">
              {/* Group header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3 h-3 rounded-full ${COLOR_DOTS[selectedGroup.color] || 'bg-blue-500'}`} />
                <h3 className="font-bold text-slate-800">{selectedGroup.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_BADGE[selectedGroup.group_type]}`}>
                  {selectedGroup.group_type}
                </span>
                <span className="ml-auto text-sm text-slate-400">{members.length} member{members.length !== 1 ? 's' : ''}</span>
              </div>

              {membersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Current members */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Members</p>
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                        <input
                          type="text"
                          value={memberSearch}
                          onChange={e => setMemberSearch(e.target.value)}
                          placeholder="Search..."
                          className="border border-slate-200 rounded-lg pl-6 pr-2 py-1 text-xs outline-none w-28"
                        />
                      </div>
                    </div>
                    {filteredMembers.length === 0 ? (
                      <div className="text-center py-8 bg-slate-50 rounded-lg">
                        <CheckCircle2 className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                        <p className="text-xs text-slate-400">No members yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {filteredMembers.map(s => (
                          <div key={s.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                                {(s.first_name || '?')[0]}
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-800">{s.first_name} {s.last_name}</p>
                                <p className="text-[10px] text-slate-400">{s.class_name || 'No class'}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveStudent(s.id)}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                              data-testid="remove-student-btn"
                            >
                              <UserMinus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add students */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Add Students</p>
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                        <input
                          type="text"
                          value={addSearch}
                          onChange={e => setAddSearch(e.target.value)}
                          placeholder="Search..."
                          className="border border-slate-200 rounded-lg pl-6 pr-2 py-1 text-xs outline-none w-28"
                          data-testid="add-student-search"
                        />
                      </div>
                    </div>
                    {availableStudents.length === 0 ? (
                      <div className="text-center py-8 bg-slate-50 rounded-lg">
                        <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-400">All students already added</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {availableStudents.slice(0, 30).map(s => (
                          <div key={s.id} className="flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
                                {(s.first_name || '?')[0]}
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-700">{s.first_name} {s.last_name}</p>
                                <p className="text-[10px] text-slate-400">{s.class_name || 'No class'}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddStudent(s.id)}
                              className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                              data-testid="add-student-btn"
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        {availableStudents.length > 30 && (
                          <p className="text-xs text-slate-400 text-center py-1">
                            {availableStudents.length - 30} more — refine search to find them
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
